import React, { useState } from 'react';
import BlockIcon from '@mui/icons-material/Block';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import PlanUpgradeModal from '../subscription/PlanUpgradeModal';
import { BUSINESS_PLAN_NAME, planAvailabilityText } from '../subscription/planLockTexts';
import styles from './AccessDenied.module.css';

const OWNER_HINT = 'Peça a quem é proprietário(a) da conta.';

/**
 * Tela de bloqueio de uma página inteira.
 *
 * Sem `preview`, é o cartão simples de sempre. Com `preview`, a tela mostra
 * por trás uma prévia FALSA e desfocada da página (dados de exemplo fixos no
 * código, nunca dados reais nem chamadas à API) e, na frente, o motivo e o
 * que fazer:
 *  - `reason="role"` (papel ou marcação): diz a quem pedir, sem botão de plano;
 *  - `reason="plan"`: diz o plano que libera e abre o atalho para Planos
 *    (PlanUpgradeModal, "Falar com a gente" no Business).
 *
 * @param {Object} props
 * @param {string} [props.title] - Título do cartão
 * @param {string} [props.description] - O motivo do bloqueio
 * @param {React.ReactNode} [props.icon] - Ícone do cartão
 * @param {React.ReactNode} [props.preview] - Prévia falsa da tela (fica desfocada atrás)
 * @param {'role'|'plan'} [props.reason='role'] - Causa do bloqueio
 * @param {string} [props.hint] - O que fazer (bloqueio de papel)
 * @param {string} [props.featureName] - Nome da tela (bloqueio de plano)
 * @param {string} [props.planName] - Plano que libera (bloqueio de plano)
 */
const AccessDenied = ({
  title = 'Acesso Restrito',
  description = 'Você não tem permissão para acessar esta página.',
  icon,
  preview,
  reason = 'role',
  hint = OWNER_HINT,
  featureName,
  planName,
}) => {
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const isPlan = reason === 'plan';

  if (!preview) {
    return (
      <div className="page-content">
        <div className={styles.container}>
          <div className={styles.card}>
            <div className={styles.iconContainer}>
              {icon || <BlockIcon style={{ fontSize: 48 }} />}
            </div>
            <h2 className={styles.title}>{title}</h2>
            <p className={styles.description}>{description}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className={styles.stage}>
        {/* Prévia só ilustrativa: fora do leitor de tela e sem foco ou clique */}
        <div className={styles.preview} aria-hidden="true">
          {preview}
        </div>
        <div className={styles.overlay}>
          <div className={styles.lockCard} role="alert">
            <div className={styles.lockIcon}>
              {icon || <LockOutlinedIcon />}
            </div>
            <span className={styles.eyebrow}>
              {isPlan ? `Plano ${planName}` : 'Bloqueado para o seu acesso'}
            </span>
            <h2 className={styles.lockTitle}>{title}</h2>
            <p className={styles.lockDescription}>
              {isPlan && featureName
                ? <><strong>{featureName}</strong> {planAvailabilityText(planName)}. {description}</>
                : description}
            </p>
            {isPlan ? (
              <button type="button" className={styles.planButton} onClick={() => setUpgradeOpen(true)}>
                {planName === BUSINESS_PLAN_NAME ? 'Falar com a gente' : 'Ver planos'}
              </button>
            ) : (
              <div className={styles.hint}>
                <SupervisorAccountOutlinedIcon fontSize="small" className={styles.hintIcon} />
                <span>{hint}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      {isPlan && (
        <PlanUpgradeModal
          isOpen={upgradeOpen}
          onClose={() => setUpgradeOpen(false)}
          featureName={featureName || title}
          type="permission"
          planName={planName}
        />
      )}
    </div>
  );
};

export default AccessDenied;
